<script lang="ts">
    import {fade, scale} from 'svelte/transition';
    import Viewer from "../common/Viewer.svelte";
    import Svg from "../common/Svg.svelte";
    import type { Attachment } from 'svelte/attachments';
    import Hammer from 'hammerjs';


    let {
        open = $bindable(),
        canZoom = false,
        src = '',
        onprev = $bindable(),
        onnext = $bindable(),
        hasNext = false,
        hasPrev = false
    }: {
        open?: boolean;
        canZoom?: boolean;
        src?: string;
        hasPrevAndNext?: boolean;
        onprev?: () => void;
        onnext?: () => void;
        hasNext?: boolean;
        hasPrev?: boolean;
    } = $props();

    let minScale = $state(0.5);
    let maxScale = $state(3.0);
    let targetScale = $state(0.5);

    const lightbox: Attachment = (element) => {
        let hammer = new Hammer(element as HTMLElement);

        hammer.get('pan').set({
            direction: Hammer.DIRECTION_HORIZONTAL,
            threshold: 10
        });

        hammer.on('panend', (ev) => {
            switch (ev.direction) {
                case Hammer.DIRECTION_LEFT:
                    if (hasNext) {
                        onnext();
                    }
                    break;
                case Hammer.DIRECTION_RIGHT:
                    if (hasPrev) {
                        onprev();
                    }
                    break;
            }
        })


    };

    $effect(() => {
        if (open) {
            document.body.classList.add('lock-scroll');
        } else {
            document.body.classList.remove('lock-scroll');
        }
    })

    function zoomIn() {
        targetScale = Math.min(targetScale + 0.2, maxScale);
    }

    function zoomOut() {
        targetScale = Math.max(targetScale - 0.2, minScale);
    }
</script>

{#if open}
    {#if canZoom}
        <div class="viewer">
            <div class="top-actions">
                <button class="close-button"
                        onclick={() => {
                    open = false;
                }}
                >
                    <Svg type="cross"/>
                </button>
            </div>

            <Viewer
                    bind:targetScale
            >
                <img
                        src={src}
                        alt="Thumbnail"
                />
            </Viewer>

            <div class="bottom-actions">
                <button class="zoom-out"
                        onclick={zoomOut}
                >
                    <Svg type="minus"/>
                </button>
                <button class="zoom-in"
                        onclick={zoomIn}
                >
                    <Svg type="plus"/>
                </button>
            </div>

            <div class="middle-buttons">
                <button class="prev-button"
                        disabled={!hasPrev}
                        onclick={onprev}
                >
                    <Svg type="chevron-left"/>
                </button>
                <button class="next-button"
                        disabled={!hasNext}
                        onclick={onnext}
                >
                    <Svg type="chevron-right"/>
                </button>

            </div>
        </div>
    {:else}
        <div class="lightbox">
            <div class="lightbox-backdrop pointer-cursor"
                 {@attach lightbox}
                 transition:fade|global={{duration: 500}}
                 onclick={() => {
                    open = false;
                }}
            ></div>
            <div class="lightbox-content"
                 {@attach lightbox}
                 in:scale|global={{duration: 500, delay: 100}}
                 out:scale|global={{duration: 500}}
            >
                <img
                        src={src}
                        alt="Thumbnail"
                />
            </div>
            <div class="pagination-buttons"
                 {@attach lightbox}
            >
                <button class="prev-button"
                        disabled={!hasPrev}
                        onclick={onprev}
                >
                    <Svg type="chevron-left"/>
                </button>
                <button class="next-button"
                        disabled={!hasNext}
                        onclick={onnext}
                >
                    <Svg type="chevron-right"/>
                </button>
            </div>
        </div>
    {/if}
{/if}

<style lang="scss">

  .viewer {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000000;
    background-color: rgba(0, 0, 0, 0.8);

    img {
      margin: auto;
      max-width: 90%;
      max-height: 200vh;
      object-fit: contain;
      border-radius: 8px;
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
    }

    .top-actions {
      position: absolute;
      top: 20px;
      right: 20px;
      display: flex;
      gap: 10px;
      z-index: 10000;

      .close-button {
        background: none;
        border: none;
        color: white;
        cursor: pointer;
        font-size: 24px;
        transition: opacity 0.2s;

        &:hover {
          opacity: .5;
        }
      }
    }

    .bottom-actions {
      position: absolute;
      bottom: 20px;
      left: 50%;
      transform: translateX(-50%);
      display: flex;
      gap: 10px;
      z-index: 10000;

      button {
        background: none;
        border: none;
        color: white;
        cursor: pointer;
        font-size: 24px;
        transition: opacity 0.2s;

        &:hover {
          opacity: .5;
        }
      }
    }

    .middle-buttons {
      position: absolute;
      top: 50%;
      left: 0;
      width: 100%;
      transform: translateY(-50%);
      display: flex;
      justify-content: space-between;
      gap: 10px;
      z-index: 10000;
      padding: 0 20px;
      pointer-events: none;
      max-width: calc(100vw - 40px);

      button {
        pointer-events: all;
        background: none;
        border: none;
        color: white;
        cursor: pointer;
        font-size: 24px;
        transition: opacity 0.2s;

        &:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        &:hover {
          opacity: .5;
        }
      }
    }
  }

  :global(body.lock-scroll) {
    overflow: hidden;
    height: 100dvh;
  }

  .lightbox {
    position: fixed;
    top: 0;
    z-index: 1000000;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;

    .lightbox-backdrop {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: rgba(0, 0, 0, 0.8);
    }

    .lightbox-content {
      position: relative;
      z-index: 1;
      max-width: 90%;
      max-height: 90%;
      overflow-y: auto;

      img {
        max-width: 100%;
        max-height: 100%;
      }
    }

    .pagination-buttons {
      position: absolute;
      inset:0;
      top: 50%;
      transform: translateY(-50%);
      display: flex;
      justify-content: space-between;
      gap: 10px;
      z-index: 10000;
      padding: 0 20px;
      width: calc(100% - 40px);
      height: fit-content;

      button {
        background: none;
        border: none;
        color: white;
        cursor: pointer;
        font-size: 24px;
        transition: opacity 0.2s;

        &:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        &:hover {
          opacity: .5;
        }
      }
    }
  }
</style>
